import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    NgZone,
    OnInit,
    ViewEncapsulation,
} from "@angular/core";
import { S25EditableAbstract } from "../s25-editable/s25.editable.abstract";
import { S25ItemI } from "../../pojo/S25ItemI";
import { S25Util } from "../../util/s25-util";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { EventService } from "../../services/event.service";
import { MultiselectModelI } from "../s25-multiselect/s25.multiselect.component";

@TypeManagerDecorator("s25-ng-editable-additional-orgs")
@Component({
    selector: "s25-ng-editable-additional-orgs",
    template: ` @if (init) {
        <div class="c-eventDetails--additionalOrgs" aria-label="Additional Organizations" tabindex="0">
            <span class="evdd-item-label">Additional Organization{{ addtlOrgs.length === 1 ? ":" : "s:" }}</span>
            <div class="org-items-container">
                @for (org of addtlOrgs; track org) {
                    <div class="s25-item-org-container">
                        <s25-item-organization [modelBean]="org" [includeTypeIcon]="true"></s25-item-organization>
                        @if (editable) {
                            <button (click)="removeOrg(org.itemId)" class="c-textButton">
                                <s25-ng-icon [type]="'close'"></s25-ng-icon>
                            </button>
                        }
                    </div>
                }
                @if (editable) {
                    <div class="additional-org-popover-wrapper">
                        <s25-ng-multiselect-search-criteria
                            [type]="'organizations'"
                            [popoverOnBody]="true"
                            [selectedItems]="addtlOrgs"
                            [(modelBean)]="multiSelectBean"
                            [customPopoverClass]="'additional-org-popover'"
                        ></s25-ng-multiselect-search-criteria>
                    </div>
                }
            </div>
        </div>
    }`,
    styles: `
        :host {
            .c-textButton {
                color: unset !important;
            }

            .evdd-item-label {
                flex: 0 0 auto;
            }

            .org-items-container {
                display: flex;
                flex-wrap: wrap;
                gap: 1em;

                s25-item-type-icon-holder-organization {
                    position: relative;
                    top: 2px;
                }

                .s25-item-name {
                    word-break: break-all;
                    overflow-wrap: break-word;
                }

                .additional-org-popover-wrapper {
                    flex-basis: 100%;

                    .multiSelectButton {
                        flex-basis: 100%;
                        background: #3273a0 !important;
                        border-color: #3273a0 !important;
                        color: #fff !important;
                    }
                }
            }
        }

        @media screen and (max-width: 823px) {
            .c-eventDetails--additionalOrgs {
                flex-direction: column;

                .org-items-container {
                    gap: 5px;
                }
            }
        }

        @media screen and (max-width: 700px) {
            .c-eventDetails--additionalOrgs {
                .org-items-container {
                    margin-inline: 10px;
                }
            }
        }

        @media screen and (max-width: 500px) {
            .c-eventDetails--additionalOrgs {
                .s25-multiselect-popup-position-header,
                s25-popover,
                .multiSelectButton,
                .ngInlineBlock {
                    width: 100%;
                }
            }
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class S25EditableAdditionalOrgsComponent extends S25EditableAbstract implements OnInit {
    @Input() itemId: string;
    @Input() addtlOrgs: S25ItemI[];
    @Input() editable: boolean;

    init: boolean = false;
    multiSelectBean: MultiselectModelI = {};

    constructor(
        private elementRef: ElementRef,
        private cd: ChangeDetectorRef,
        private zone: NgZone,
    ) {
        super(elementRef, cd, zone);
        this.elementRef.nativeElement.angBridge = this;
    }

    getType = () => "additional orgs";

    ngOnInit() {
        super.ngOnInit();
        this.sortOrgsAlphabetically();
        this.zone.run(() => {
            this.multiSelectBean.placeholder = "Organization Name";
            this.multiSelectBean.items = this.addtlOrgs;
            this.multiSelectBean.onDone = () => {
                const eventId = S25Util.toInt(this.itemId);
                const checkedOrgIds = this.multiSelectBean.addedItems
                    ? this.multiSelectBean.addedItems.map((org) => org.itemId)
                    : [];
                const uncheckedOrgIds = this.multiSelectBean.removedItems
                    ? this.multiSelectBean.removedItems.map((org) => org.itemId)
                    : [];

                return EventService.updateOrganizations([eventId], checkedOrgIds, uncheckedOrgIds).then(() => {
                    this.sortOrgsAlphabetically();

                    this.cd.detectChanges();
                });
            };
        });

        this.init = true;
        this.cd.detectChanges();
    }

    removeOrg(orgId: number) {
        const eventId = S25Util.toInt(this.itemId);

        return EventService.updateOrganizations([eventId], [], [orgId]).then(() => {
            this.addtlOrgs = this.addtlOrgs.filter((el: S25ItemI) => el.itemId !== orgId);
            this.multiSelectBean.selectedItems = this.addtlOrgs;

            this.cd.detectChanges();
        });
    }

    sortOrgsAlphabetically() {
        if (this.addtlOrgs.length > 0) {
            this.addtlOrgs = this.addtlOrgs.sort((a, b) => {
                if (a.itemName > b.itemName) return 1;
                if (a.itemName < b.itemName) return -1;

                return 0;
            });
        }
    }
}
